import React from 'react';
import { HandThumbUpIcon, HandThumbDownIcon, ChatBubbleOvalLeftIcon } from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  HandThumbDownIcon as HandThumbDownSolidIcon,
} from '@heroicons/react/24/solid';
import type { Comment } from '../../types/comments';

interface ReactionRowProps {
  comment: Comment;
  /** Reactions are gated to authenticated viewers; null when unknown. */
  canReact: boolean;
  /** Reply affordance is hidden for replies (one-level threads). */
  canReply: boolean;
  onToggleLike: () => void;
  onToggleDislike: () => void;
  onReply?: () => void;
}

/**
 * Pill-shaped chips for like/dislike with counts plus a Reply trigger.
 * Read-only emoji buckets render after dislike when present (no picker).
 */
export default function ReactionRow({
  comment,
  canReact,
  canReply,
  onToggleLike,
  onToggleDislike,
  onReply,
}: ReactionRowProps) {
  const { like, dislike, emoji } = comment.reactions;
  return (
    <div className="flex flex-wrap gap-1 items-center">
      <ReactionChip
        active={like.reacted}
        count={like.count}
        disabled={!canReact}
        ariaLabel={like.reacted ? 'Remove like' : 'Like comment'}
        onClick={onToggleLike}
        accent="like"
      >
        {like.reacted ? (
          <HandThumbUpSolidIcon className="w-3.5 h-3.5" aria-hidden="true" />
        ) : (
          <HandThumbUpIcon className="w-3.5 h-3.5" aria-hidden="true" />
        )}
      </ReactionChip>
      <ReactionChip
        active={dislike.reacted}
        count={dislike.count}
        disabled={!canReact}
        ariaLabel={dislike.reacted ? 'Remove dislike' : 'Dislike comment'}
        onClick={onToggleDislike}
        accent="dislike"
      >
        {dislike.reacted ? (
          <HandThumbDownSolidIcon className="w-3.5 h-3.5" aria-hidden="true" />
        ) : (
          <HandThumbDownIcon className="w-3.5 h-3.5" aria-hidden="true" />
        )}
      </ReactionChip>

      {emoji.map((bucket) => (
        <span
          key={bucket.emoji}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-medium text-text-primary"
          title={`${bucket.emoji}: ${bucket.count}`}
        >
          <span className="text-sm leading-none" aria-hidden="true">
            {bucket.emoji}
          </span>
          <span>{bucket.count}</span>
        </span>
      ))}

      {canReply && onReply && (
        <button
          type="button"
          onClick={onReply}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium text-text-primary hover:bg-[rgba(3,2,19,0.06)] dark:hover:bg-white/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
        >
          <ChatBubbleOvalLeftIcon className="w-3.5 h-3.5" aria-hidden="true" />
          Reply
        </button>
      )}
    </div>
  );
}

interface ReactionChipProps {
  active: boolean;
  count: number;
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
  accent: 'like' | 'dislike';
  children: React.ReactNode;
}

function ReactionChip({
  active,
  count,
  disabled,
  ariaLabel,
  onClick,
  accent,
  children,
}: ReactionChipProps) {
  const accentClass = active
    ? accent === 'like'
      ? 'text-primary-500'
      : 'text-error-500'
    : 'text-text-primary';
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium ${accentClass} hover:bg-[rgba(3,2,19,0.06)] dark:hover:bg-white/[0.06] active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 transition-[background-color,transform,color] duration-fast ease-out-strong`}
    >
      {children}
      <span className="tabular-nums">{count}</span>
    </button>
  );
}
